import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Shield, Linkedin, Facebook, Instagram, Youtube } from 'lucide-react';
import { SiteHeader } from '@/components/marketing/SiteHeader';
import { FooterNewsletter } from '@/components/marketing/FooterNewsletter';
import { DeferredChatWidget } from '@/components/support/DeferredChatWidget';
import { DeferredChrome } from '@/components/marketing/DeferredChrome';
const officialLogo = '/assets/hirebee-logo-official.svg';

interface MarketingLayoutProps {
  children: ReactNode;
  className?: string;
}

// Server component: static shell + footer render as HTML with no hydration.
// Interactivity lives in two small client islands: <SiteHeader/> and
// <FooterNewsletter/>.
export function MarketingLayout({ children, className = "" }: MarketingLayoutProps) {
  return (
    <div className={`min-h-screen bg-background isolate ${className}`}>
      {/* Skip-to-content link — visible only on keyboard focus (a11y) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[200] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Interactive header island */}
      <SiteHeader />

      {/* Main content — offset by the fixed header's real height (nav, plus the promo
          banner, which is visible by default), kept in sync by SiteHeader's ResizeObserver
          via --site-header-height. pt-28/7rem is only the pre-hydration/no-JS fallback —
          sized to comfortably cover nav+banner since that's the default (common) state. */}
      <main id="main-content" className="pt-28" style={{ paddingTop: 'var(--site-header-height, 7rem)' }}>{children}</main>

      {/* Marketing Footer (static, server-rendered) */}
      {/* Marketing Footer */}
      <footer className="bg-[#F8FAFC] dark:bg-muted/40 border-t border-border/50 mt-16 relative overflow-hidden">
        <div className="container mx-auto py-10 relative">
          {/* Header Section with Logo and Newsletter */}
          <div className="grid lg:grid-cols-3 gap-8 mb-8">
            {/* Company Branding */}
            <div className="lg:col-span-1 space-y-6">
              <Link to="/" className="inline-block transition-transform duration-200 hover:scale-105">
                <img
                  src={officialLogo}
                  alt="Hirebee Logo" width={148} height={56}
                  className="h-10 w-auto object-contain"
                />
              </Link>
              <p className="text-muted-foreground leading-relaxed max-w-sm">
                Transform your HR operations with our intelligent, all-in-one platform trusted by 1,000+ companies worldwide.
              </p>
              
              {/* Contact Information */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <div className="p-1.5 rounded-lg bg-primary/10">
                    <Mail className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <a href="mailto:hello@hirebee.ai" className="hover:text-foreground transition-colors">
                    hello@hirebee.ai
                  </a>
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <div className="p-1.5 rounded-lg bg-primary/10">
                    <Phone className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <a href="tel:+14708099036" className="hover:text-foreground transition-colors">
                    +1 (470) 809-9036
                  </a>
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <div className="p-1.5 rounded-lg bg-primary/10">
                    <MapPin className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <span>75 5th St NW, Atlanta, GA 30308, USA</span>
                </div>
              </div>

              {/* Security Badges */}
              <div className="flex items-center gap-4 pt-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950/20 rounded-full border border-emerald-200 dark:border-emerald-800">
                  <Shield className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">SOC 2 Certified</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/20 rounded-full border border-blue-200 dark:border-blue-800">
                  <Shield className="h-3.5 w-3.5 text-blue-600" />
                  <span className="text-xs font-medium text-blue-700 dark:text-blue-400">GDPR Ready</span>
                </div>
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="lg:col-span-2 lg:pl-12">
              <div className="bg-gradient-to-br from-primary/5 via-accent/5 to-transparent border border-border/50 rounded-2xl p-8">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0 mb-6">
                  <div>
                    <h3 className="text-xl font-bold mb-2">Stay Updated</h3>
                    <p className="text-muted-foreground text-sm">
                      Get the latest HR insights, product updates, and exclusive resources delivered to your inbox.
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-primary">25,000+</div>
                    <div className="text-xs text-muted-foreground">Subscribers</div>
                  </div>
                </div>
                
                <FooterNewsletter />
                
                <p className="text-xs text-muted-foreground">
                  Monthly insights • No spam • Unsubscribe anytime
                </p>
              </div>
            </div>
          </div>

          {/* Links Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
            {/* Product */}
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Product</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link to="/features/hr-management" className="hover:text-foreground transition-colors">HR Management</Link></li>
                <li><Link to="/features/payroll" className="hover:text-foreground transition-colors">Payroll</Link></li>
                <li><Link to="/features/leave-attendance/time-tracking" className="hover:text-foreground transition-colors">Time Tracking</Link></li>
                <li><Link to="/features/reporting-and-analytics" className="hover:text-foreground transition-colors">Analytics</Link></li>
                <li><Link to="/features/advanced-analytics-ai-insights" className="hover:text-foreground transition-colors">AI-Powered HR</Link></li>
                <li><Link to="/features/performance-management-and-goal" className="hover:text-foreground transition-colors">Performance</Link></li>
              </ul>
            </div>

            {/* Solutions */}
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Solutions</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link to="/business-type-recruitment-software-for-startups" className="hover:text-foreground transition-colors">Startups</Link></li>
                <li><Link to="/business-type-enterprise-recruitment-software" className="hover:text-foreground transition-colors">Enterprises</Link></li>
                <li><Link to="/business-type-staffing-agency-recruiting-software" className="hover:text-foreground transition-colors">Staffing Agency</Link></li>
                <li><Link to="/solutions/remote-teams" className="hover:text-foreground transition-colors">Remote Teams</Link></li>
                <li><Link to="/solutions/assessment" className="hover:text-foreground transition-colors">Find My Solution</Link></li>
                <li><Link to="/pricing" className="hover:text-foreground transition-colors">Pricing Plans</Link></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Resources</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link></li>
                <li><Link to="/resources" className="hover:text-foreground transition-colors">Resource Library</Link></li>
                <li><Link to="/customer-stories" className="hover:text-foreground transition-colors">Customer Stories</Link></li>
                <li><Link to="/integrations" className="hover:text-foreground transition-colors">Integrations</Link></li>
                <li><Link to="/api-docs" className="hover:text-foreground transition-colors">API Docs</Link></li>
                <li><Link to="/status" className="hover:text-foreground transition-colors">Status Page</Link></li>
                <li><Link to="/hr-terms" className="hover:text-foreground transition-colors">HR Glossary</Link></li>
              </ul>
            </div>

            {/* Tools */}
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Free Tools</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link to="/tools/roi-calculator" className="hover:text-foreground transition-colors">ROI Calculator</Link></li>
                <li><Link to="/tools/salary-calculator" className="hover:text-foreground transition-colors">Salary Calculator</Link></li>
                <li><Link to="/tools/resume-analyzer" className="hover:text-foreground transition-colors">Resume Analyzer</Link></li>
                <li><Link to="/tools/hr-audit" className="hover:text-foreground transition-colors">HR Audit Tool</Link></li>
                <li><Link to="/hirebee-vs-competitors" className="hover:text-foreground transition-colors">Compare Features</Link></li>
                <li><Link to="/compare" className="hover:text-foreground transition-colors">Compare ATS Tools</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Company</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link to="/our-story" className="hover:text-foreground transition-colors">About Us</Link></li>
                <li><Link to="/team" className="hover:text-foreground transition-colors">Our Team</Link></li>
                <li><Link to="/careers" className="hover:text-foreground transition-colors">Careers</Link></li>
                <li><Link to="/roadmap" className="hover:text-foreground transition-colors">Roadmap</Link></li>
                <li><Link to="/press" className="hover:text-foreground transition-colors">Press Kit</Link></li>
                <li><Link to="/community" className="hover:text-foreground transition-colors">Community</Link></li>
                <li><Link to="/contacts" className="hover:text-foreground transition-colors">Contact Us</Link></li>
              </ul>
            </div>

            {/* Support & Legal */}
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Support & Legal</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link to="/help" className="hover:text-foreground transition-colors">Help Center</Link></li>
                <li><Link to="/faq" className="hover:text-foreground transition-colors">FAQ</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-use" className="hover:text-foreground transition-colors">Terms of Service</Link></li>
                <li><Link to="/cookie-policy" className="hover:text-foreground transition-colors">Cookie Policy</Link></li>
                <li><Link to="/refund-policy" className="hover:text-foreground transition-colors">Refund Policy</Link></li>
                <li><Link to="/dpa" className="hover:text-foreground transition-colors">DPA</Link></li>
                <li><Link to="/security" className="hover:text-foreground transition-colors">Security</Link></li>
                <li><Link to="/compliance" className="hover:text-foreground transition-colors">Compliance</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="pt-6 border-t border-border/50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Copyright */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <p>© 2026 Hirebee.ai. All rights reserved.</p>
                <Link to="/sitemap" className="hover:text-foreground transition-colors">Sitemap</Link>
                <Link to="/changelog" className="hover:text-foreground transition-colors">Changelog</Link>
              </div>

              {/* Social Media */}
              <div className="flex items-center gap-2">
                <a href="https://www.linkedin.com/company/hirebee-ai/" target="_blank" rel="noopener noreferrer" aria-label="Hirebee on LinkedIn" className="p-2 rounded-lg text-muted-foreground hover:text-primary transition-colors">
                  <Linkedin className="h-4 w-4" aria-hidden="true" />
                </a>
                <a href="https://www.facebook.com/HirebeeInc/" target="_blank" rel="noopener noreferrer" aria-label="Hirebee on Facebook" className="p-2 rounded-lg text-muted-foreground hover:text-primary transition-colors">
                  <Facebook className="h-4 w-4" aria-hidden="true" />
                </a>
                <a href="https://www.instagram.com/hirebee.ai/" target="_blank" rel="noopener noreferrer" aria-label="Hirebee on Instagram" className="p-2 rounded-lg text-muted-foreground hover:text-primary transition-colors">
                  <Instagram className="h-4 w-4" aria-hidden="true" />
                </a>
                <a href="https://www.youtube.com/@hirebeeai8174" target="_blank" rel="noopener noreferrer" aria-label="Hirebee on YouTube" className="p-2 rounded-lg text-muted-foreground hover:text-primary transition-colors">
                  <Youtube className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* AI support chat — marketing pages only */}
      <DeferredChatWidget />

      {/* Cookie consent — shown once per visitor (localStorage-gated) */}

      {/* Toast host for client-island actions (e.g. demo booking form validation) */}
      <DeferredChrome />
    </div>
  );
}
